/**
 * Types for the `/api/v2/precision/history/*` endpoints that back History →
 * Programming and History → Prescription.
 *
 * The two listings share one page envelope; the detail objects are the
 * customer-prescription entities themselves (a prescribed service, a
 * prescription with its services and lab panels). Field names are the
 * backend's, including its `customerFirstname` casing, so the response can be
 * used without a translation step — the row mappers in `history/` build the
 * table rows from these.
 */

export interface HistoryListParams {
  page: number;
  rows: number;
  /** Narrows the listing to one client. Omitted → every client of the company. */
  userid?: string | null;
}

export interface HistoryPage<T> {
  /** Rows in this page. */
  count: number;
  /** Rows across all pages. */
  totalCount: number;
  currentPage: number;
  sortOrder: string;
  sortDirection: string;
  data: T[];
}

/** One row of `GET /history/services`. */
export interface HistoryServiceListItem {
  id: string;
  customerID: string;
  userID: string;
  /** Service type code, 1–8 — see `enReportServiceType`. */
  serviceType: number;
  dateCreated: string;
  startDate: string | null;
  endDate: string | null;
  logicName: string | null;
  serviceName: string;
  serviceDescription: string | null;
  companyName: string | null;
  companyEmail: string | null;
  companyIdentity: string | null;
  customerFirstname: string | null;
  customerLastname: string | null;
  customerEmail: string | null;
  customerIdentity: string | null;
  /** 1 active, 0 inactive — drives the listing's Status column. */
  active: number;
  /** ISO timestamp; null while the service is active. */
  deactivationDate: string | null;
  /** 0 active, 1 expired. */
  expired: number;
}

/**
 * `GET /history/services/{id}` — the report's `ServiceContent` for one
 * prescribed service, with the values prescribed for this client (the
 * earlier build returned the saved-service entity with template values).
 * Carries the client but no record id.
 */
export interface HistoryServiceDetail {
  customerID: string;
  /** The client's name, spelled differently here than in the listings. */
  firstName: string | null;
  lastName: string | null;
  serviceName: string;
  /** Display label, e.g. "Longevity treatments". */
  serviceType: string;
  /** Service type code, 1–8 — see `enReportServiceType`. */
  serviceTypeCode: number;
  description: string | null;
  imageURL: string | null;
  reportSvcID: string | null;
  userServiceID: string | null;
  /** `dd-MM-yyyy`, as the report formats it. */
  prescriptionPeriod: { startDate: string; endDate?: string | null } | null;
  activeCompounds: { name: string; value: string; unit: string }[] | null;
  attributes: { name: string; value: string; unit: string }[] | null;
  pnoeBenefits: { name: string; text: string }[] | null;
  additionalBenefits: { name: string; text: string }[] | null;
  details: { name: string; text: string | null }[] | null;
  /** Null for a manually prescribed service. */
  rational: { list?: any[] } | null;
  /** 1 active, 0 inactive. */
  active: number;
  /** ISO timestamp; null while the service is active. */
  deactivationDate: string | null;
  /** The prescription this service belongs to; absent on older records. */
  customerPrescriptionID?: string | null;
}

/** One row of `GET /history/prescription`. */
export interface HistoryPrescriptionListItem {
  id: string;
  customerID: string;
  userID: string;
  /** 1 auto-generated, 2 auto-generated then edited, 3 manual. */
  sourceType: number;
  dateCreated: string;
  companyName: string | null;
  companyEmail: string | null;
  companyIdentity: string | null;
  customerFirstname: string | null;
  customerLastname: string | null;
  customerEmail: string | null;
  customerIdentity: string | null;
  servicesCount: number;
  labsCount: number;
  /** 1 active, 0 inactive — the affiliate's switch on the detail page. */
  active: number;
  /** 0 not expired, 1 expired. Expired wins over `active`. */
  expired: number;
}

/**
 * `GET /history/prescription/{id}` — the object `PrescriptionDetailsView`
 * renders: services and lab panels included.
 */
export interface HistoryPrescriptionDetail {
  id: string;
  customerID: string;
  userID: string;
  sourceType: number;
  services: any[];
  labPanels: any[];
  expired: number;
  isExpired: boolean;
  startDate: string | null;
  endDate: string | null;
  dateCreated: string;
  dateUpdated: string | null;
  active: number;
  isActive: boolean;
}

/** One row of `GET /history/inbody` — one InBody test (a batch of biomarkers). */
export interface HistoryInbodyListItem {
  id: string;
  customerID: string;
  userID: string;
  /** Device model, e.g. "270S". */
  device: string | null;
  batchID: string;
  /** When the test was taken (date only). */
  testDate: string | null;
  /** When it was imported. */
  dateCreated: string;
  /** Biomarkers in the batch. */
  count: number;
  companyName: string | null;
  companyEmail: string | null;
  companyIdentity: string | null;
  customerFirstname: string | null;
  customerLastname: string | null;
  customerEmail: string | null;
  customerIdentity: string | null;
}

/** One biomarker of `GET /history/inbody/{id}`. Lower-case keys are the backend's. */
export interface HistoryInbodyBiomarker {
  value: number | null;
  batchid: string;
  customerid: string;
  /** The test date. */
  datecreated: string | null;
  /** Biomarker category code — see `enBiomarkerCategory`. */
  category: number;
  /** Test kind code — 4 is "Body composition". */
  testkind: number;
  name: string;
  shortname: string | null;
  /** Reference range, free text. */
  label: string | null;
  key: string;
  unit: string | null;
}

export type ServiceHistoryStatus = "active" | "expired" | "inactive";

/** A Programming listing row, built from `HistoryServiceListItem`. */
export interface ServiceHistoryRow {
  id: string;
  /** ISO date; drives the Date Created column. */
  dateCreated: string;
  clientId: string;
  clientName: string;
  /** Display name of the service type, e.g. "Longevity treatments". */
  serviceType: string;
  serviceName: string;
  status: ServiceHistoryStatus;
}

/**
 * "Firstname Lastname", or "" when the backend sent neither — table cells
 * fall back to an em dash themselves and `ClientBadge` hides on an empty name.
 *
 * The listings spell the fields `customerFirstname`/`customerLastname` and the
 * service detail spells them `firstName`/`lastName`; both are read here so
 * callers don't each have to know which endpoint they came from.
 */
export const historyClientName = (item: {
  customerFirstname?: string | null;
  customerLastname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): string =>
  [item.customerFirstname ?? item.firstName, item.customerLastname ?? item.lastName]
    .filter((part) => part && part.trim())
    .join(" ");
